import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ArrowRight, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { NeuralField } from "../components/NeuralField";
import LegalLink from "../components/LegalLink";
import SignupConsent from "../components/SignupConsent";
import { CaptchaStep, VerifyCodeStep } from "../components/CaptchaDialog";
import { ForgotPassword } from "../components/ForgotPassword";
import { LawfirmAuthBackdrop, LawfirmTopBar, LawfirmAuthHero } from "./lawfirm-marketing/AuthHero";
import { DISPLAY as LF_DISPLAY, BODY as LF_BODY } from "./lawfirm-marketing/ui";

const BG = "https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const GOLD = "#c9a227";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } } };
const rise = { hidden: { y: 28, opacity: 0 }, show: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };
const slide = { hidden: { x: 24, opacity: 0 }, show: { x: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

export default function Login({ initialMode = "login", portal = "lawfirm" }) {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState(initialMode === "signup" ? "signup" : "login"); // login | signup | pending
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [firmName, setFirmName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [notMember, setNotMember] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [codeStep, setCodeStep] = useState(false);
  const [pendingReg, setPendingReg] = useState(null);
  const name = `${firstName} ${lastName}`.trim();
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = 0.6;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  // /x1 access gate: only allowlisted IPs may even see the platform login; others get 404.
  const [gate, setGate] = useState(portal === "platform" ? "checking" : "ok");
  useEffect(() => {
    if (portal !== "platform") return;
    api.get("/x1-gate").then((r) => setGate(r.data.allowed ? "ok" : "blocked")).catch(() => setGate("ok"));
  }, []);

  const openCaptcha = (e) => {
    e.preventDefault(); setError(""); setNotMember(false);
    if (mode === "signup") {
      const pw = password;
      if (pw.length < 12 || !/[A-Z]/.test(pw) || !/[a-z]/.test(pw) || !/[0-9]/.test(pw) || !/[^A-Za-z0-9]/.test(pw)) {
        setError("Password must be at least 12 characters and include an uppercase letter, a lowercase letter, a number and a symbol.");
        return;
      }
    }
    setCaptchaOpen(true);
  };

  const doAttempt = async (captcha, extra) => {
    if (mode === "login") {
      try {
        const hasExtra = extra && (extra.mfa_code || extra.recovery_code);
        const u = await login(email.trim(), password, captcha, "firm", { ...(hasExtra ? extra : {}), portal });
        if (u.platform_role === "owner") { nav("/platform"); return { done: true }; }
        if (u.tenant_role === "client") { nav("/client"); return { done: true }; }
        if (u.mfa_setup_required) { nav("/focused"); return { done: true }; }
        try { const { data } = await api.get("/matters"); nav(data.length ? "/focused" : "/firm"); }
        catch { nav("/focused"); }
        return { done: true };
      } catch (err) {
        const d = err.response?.data?.detail;
        if (d && typeof d === "object" && d.code === "not_firm_member") { setNotMember(true); return { close: true }; }
        throw err;
      }
    }
    const reg = { firm_name: firmName.trim(), name: name.trim(), email: email.trim().toLowerCase(), password };
    await api.post("/auth/register-firm/send-code", { ...reg, ...(captcha || {}) });
    setPendingReg(reg);
    setCodeStep(true);
    return { close: true };
  };

  const labelCls = "block text-[10.5px] uppercase tracking-[0.18em] text-white/45 mb-1.5 font-display";
  const lf = portal === "lawfirm";
  const ACC = lf ? "#38bdf8" : GOLD;
  const inputCls = `w-full rounded-lg px-4 h-12 text-[14px] text-white bg-white/[0.03] border border-white/10 outline-none transition-colors duration-200 hover:bg-white/[0.05] focus:bg-white/[0.07] ${lf ? "focus:border-sky-400/70 rounded-xl" : "focus:border-[#c9a227]/70"} placeholder:text-white/25`;

  if (portal === "platform" && gate === "checking") return <div className="h-screen w-full bg-[#050505]" />;
  if (portal === "platform" && gate === "blocked") {
    return (
      <div className="min-h-screen bg-[#0b0d10] flex flex-col items-center justify-center gap-2 text-center px-6" data-testid="page-not-found">
        <span className="font-editorial text-4xl text-white">Page not found</span>
        <span className="text-white/50 text-[14px]">The page you're looking for doesn't exist.</span>
      </div>
    );
  }

  return (
    <div className={`relative h-screen w-full overflow-hidden text-white ${lf ? "bg-[#06070b]" : "bg-[#050505]"}`} style={lf ? LF_BODY : undefined} data-testid="login-page">
      <div className="absolute bottom-5 right-6 lg:right-10 z-30">
        <LegalLink inline ctx={lf ? "lawfirm" : "firm"} tone={lf ? "blue" : "gold"} />
      </div>
      {lf ? <><LawfirmAuthBackdrop /><LawfirmTopBar /></> : (<>
      {/* Ambient architectural background */}
      <motion.div
        aria-hidden
        className="absolute inset-0"
        style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: [1.08, 1.14, 1.08], opacity: 0.5 }}
        transition={{ opacity: { duration: 2 }, scale: { duration: 28, repeat: Infinity, ease: "easeInOut" } }}
      />
      {/* Cinematic grade + directional darkening for legibility */}
      <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,5,5,0.96) 0%, rgba(5,5,5,0.72) 42%, rgba(5,5,5,0.35) 70%, rgba(5,5,5,0.15) 100%)" }} />
      <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.55), rgba(5,5,5,0) 30%, rgba(5,5,5,0.75))" }} />
      {/* Backlight glow (brand green + gold) */}
      <div aria-hidden className="absolute -top-40 -right-32 w-[46rem] h-[46rem] rounded-full blur-[130px] pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.16), transparent 62%)" }} />
      <div aria-hidden className="absolute top-1/3 -left-40 w-[42rem] h-[42rem] rounded-full blur-[150px] pointer-events-none" style={{ background: "radial-gradient(circle, rgba(58,90,64,0.24), transparent 60%)" }} />
      {/* Film grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
      {/* Hairline frame */}
      <div aria-hidden className="absolute inset-4 sm:inset-6 rounded-2xl border border-white/[0.06] pointer-events-none" />
      </>)}

      <div className="relative z-10 h-full grid grid-cols-1 lg:grid-cols-12">
        {/* Hero */}
        {lf ? <LawfirmAuthHero mode={mode} /> : (
        <motion.div variants={stagger} initial="hidden" animate="show" className="hidden lg:flex relative overflow-hidden col-span-7 flex-col justify-between p-14 xl:p-20" style={{ background: "#cececb" }}>
          <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.02) brightness(1.12)", mixBlendMode: "luminosity", opacity: 0.6 }} poster="/city-poster.jpg" autoPlay muted loop playsInline preload="auto" data-testid="city-video">
            <source src="/city-bg.webm" type="video/webm" />
            <source src="/city-bg.mp4" type="video/mp4" />
          </video>
          <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(206,206,203,0.28), rgba(198,198,195,0.62))" }} />
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 46% 46%, rgba(232,232,229,0.5), rgba(206,206,203,0) 55%)" }} />
          <NeuralField light className="absolute inset-0 w-full h-full opacity-70" />
          <motion.div variants={rise} className="relative z-10 flex items-center gap-3.5">
            <img src="/lexarra-logo.png" alt="Lexarra" data-testid="brand-logo" className="w-14 h-14 rounded-xl object-cover shadow-lg shadow-black/25" />
            <div className="leading-none">
              <div className="font-editorial font-medium text-[30px] tracking-tight text-neutral-900">Lexarra</div>
              <div className="text-[10px] uppercase tracking-[0.28em] mt-1 text-neutral-600">The smarter path to Resolution</div>
            </div>
          </motion.div>

          <motion.div variants={rise} className="relative z-10 flex items-center gap-3 text-[12px] text-neutral-600 font-display tracking-wide">
            {["Your roster", "Your clients", "Your matters"].map((t, i) => (
              <React.Fragment key={t}>
                {i > 0 && <span className="text-neutral-500">·</span>}
                <span>{t}</span>
              </React.Fragment>
            ))}
          </motion.div>
        </motion.div>
        )}

        {/* Glass form panel */}
        <motion.div
          variants={slide} initial="hidden" animate="show"
          className={`col-span-1 lg:col-span-5 flex items-center justify-center px-6 sm:px-10 lg:px-14 h-full overflow-y-auto ${lf ? "pt-20 pb-14 lg:py-0 lg:bg-[#07080d]/80 lg:backdrop-blur-2xl lg:border-l lg:border-white/[0.08]" : "lg:bg-black/75 lg:backdrop-blur-2xl lg:border-l lg:border-white/10"}`}
        >
          {mode === "pending" ? (
            <div className="w-full max-w-sm text-center" data-testid="signup-pending">
              <div className="w-16 h-16 rounded-2xl bg-primary/12 border border-primary/30 flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={28} className="text-primary" /></div>
              <h2 className="font-editorial text-4xl tracking-tight mb-3">Firm submitted</h2>
              <p className="text-[14px] text-white/55 mb-8 font-display leading-relaxed">Your firm is pending approval by the platform administrator. You'll be able to sign in as soon as it's approved.</p>
              <button onClick={() => nav("/lawfirm/login")} className="text-[14px] lift" style={{ color: ACC }} data-testid="back-to-login">Back to sign in</button>
            </div>
          ) : codeStep ? (
            <div className="w-full max-w-sm" data-testid="firm-verify-code-step">
              <VerifyCodeStep email={pendingReg?.email} submitLabel="Verify & submit firm"
                onBack={() => setCodeStep(false)}
                onResend={() => api.post("/auth/signup/resend-code", { realm: "firm", email: pendingReg?.email })}
                onSubmit={async (code) => { await api.post("/auth/register-firm", { ...pendingReg, code }); setCodeStep(false); setMode("pending"); }}
                theme={{ accent: ACC, btnBg: ACC, btnText: "#000", title: "text-white", text: "text-white/55", input: inputCls, link: "text-white/50 hover:text-white" }} />
            </div>
          ) : forgotOpen ? (
            <div className="w-full max-w-sm" data-testid="firm-forgot-panel">
              <ForgotPassword client={api} base="/auth" onBack={() => setForgotOpen(false)} inputClass={inputCls}
                btnClass="w-full rounded-lg h-12 text-[14px] font-semibold text-black flex items-center justify-center gap-2 disabled:opacity-60"
                btnStyle={{ background: ACC }} accentClass={lf ? "text-sky-300 hover:text-sky-200" : "text-[#C9A227] hover:text-[#b08d20]"} mutedClass="text-white/50"
                headingClass={lf ? "text-[2rem] font-semibold tracking-[-0.03em]" : "font-editorial text-4xl tracking-tight"} />
            </div>
          ) : captchaOpen ? (
            <div className="w-full max-w-sm" data-testid="firm-captcha-step">
              <CaptchaStep onBack={() => setCaptchaOpen(false)} onAttempt={doAttempt}
                subtitle={mode === "login" ? "One quick check before we sign you in." : "One quick check before we submit your firm."}
                submitLabel={mode === "login" ? "Verify & sign in" : "Verify & submit"}
                theme={{ accent: ACC, btnText: "#000", title: "text-white", text: "text-white/55", input: inputCls, link: "text-white/50 hover:text-white" }} />
            </div>
          ) : (
            <motion.form
              variants={stagger} initial="hidden" animate="show"
              onSubmit={openCaptcha} className="w-full max-w-sm" data-testid={mode === "login" ? "login-form" : "signup-form"}
            >
              {lf ? null : <motion.div variants={rise} className="lg:hidden flex items-center gap-3 mb-10">
                <img src="/lexarra-logo.png" alt="Lexarra" className="w-11 h-11 rounded-xl object-cover" />
                <div><div className="font-editorial font-medium text-xl">Lexarra</div><div className="text-[9px] uppercase tracking-[0.24em]" style={{ color: ACC }}>The smarter path to Resolution</div></div>
              </motion.div>}

              <motion.h2 variants={rise} style={lf ? LF_DISPLAY : undefined} className={`${lf ? "text-[2rem] font-semibold tracking-[-0.03em]" : "font-editorial text-4xl tracking-tight"} mb-1.5`}>{mode === "login" ? (lf ? "Firm sign in" : "Sign in") : "Register your firm"}</motion.h2>
              <motion.p variants={rise} className="text-[14px] text-white/50 mb-8 font-display">{mode === "login" ? "Access your firm's litigation matters." : "Create a firm workspace. A platform administrator will approve it."}</motion.p>

              {mode === "signup" && (<>
                <motion.div variants={rise} className="mb-4">
                  <label className={labelCls}>Firm name</label>
                  <input data-testid="signup-firm" aria-label="Firm name" value={firmName} onChange={(e) => setFirmName(e.target.value)} className={inputCls} required />
                </motion.div>
                <motion.div variants={rise} className="mb-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>First name</label>
                    <input data-testid="signup-first-name" aria-label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputCls} required />
                  </div>
                  <div>
                    <label className={labelCls}>Last name</label>
                    <input data-testid="signup-last-name" aria-label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputCls} required />
                  </div>
                </motion.div>
              </>)}

              <motion.div variants={rise} className="mb-4">
                <label className={labelCls}>Email</label>
                <input data-testid="login-email" aria-label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} autoComplete="username" required />
              </motion.div>

              <motion.div variants={rise} className="mb-2">
                <label className={labelCls}>Password</label>
                <div className="relative">
                  <input data-testid="login-password" aria-label="Password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} pr-12`} autoComplete={mode === "login" ? "current-password" : "new-password"} required />
                  <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-password" aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                    {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </motion.div>

              {notMember ? (
                <motion.div variants={rise} className="text-[13px] text-red-400 mt-3 mb-1" data-testid="login-not-member">
                  No account found. If you'd like to join Lexarra as a law firm, <a href="/lawfirm/register" className="underline font-medium" style={{ color: ACC }} data-testid="join-firm-link">please click here</a>.
                </motion.div>
              ) : error && <motion.div variants={rise} className="text-[13px] text-red-400 mt-3 mb-1" data-testid="login-error">{error}</motion.div>}

              <motion.button variants={rise} whileTap={{ scale: 0.98 }} type="submit" data-testid={mode === "login" ? "login-submit" : "signup-submit"}
                className={`w-full mt-5 h-12 text-[14px] font-semibold flex items-center justify-center gap-2 transition-[background-color,box-shadow,transform] duration-200 disabled:opacity-60 ${lf ? "rounded-full text-[#04121c] bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-500 shadow-[0_0_36px_-8px_rgba(56,189,248,0.8)] hover:shadow-[0_0_50px_-6px_rgba(56,189,248,0.95)]" : "rounded-lg text-black"}`}
                style={lf ? undefined : { background: GOLD, boxShadow: "0 8px 30px rgba(201,162,39,0.28)" }}
                onMouseEnter={(e) => { if (!lf) e.currentTarget.style.background = "#b08d20"; }}
                onMouseLeave={(e) => { if (!lf) e.currentTarget.style.background = GOLD; }}>
                {mode === "login" ? "Sign in" : "Submit firm for approval"} <ArrowRight size={16} />
              </motion.button>

              {mode === "signup" && <SignupConsent ctx={lf ? "lawfirm" : "firm"} className="mt-5" linkStyle={{ color: ACC }} />}

              <motion.p variants={rise} className="text-[13px] text-white/45 mt-7 text-center font-display">
                {mode === "login" ? (<>New firm? <button type="button" onClick={() => nav("/lawfirm/register")} className="hover:text-white transition-colors" style={{ color: ACC }} data-testid="go-signup">Register here</button></>)
                  : (<>Already approved? <button type="button" onClick={() => nav("/lawfirm/login")} className="hover:text-white transition-colors" style={{ color: ACC }} data-testid="go-login">Sign in</button></>)}
              </motion.p>
              {mode === "login" && (
                <motion.p variants={rise} className="text-[12.5px] text-white/40 mt-2 text-center font-display">
                  <button type="button" onClick={() => setForgotOpen(true)} data-testid="firm-forgot-link" className="hover:text-white/80 transition-colors">Forgot your password?</button>
                </motion.p>
              )}
            </motion.form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
